// OHMNAI API Service - Connects frontend to backend

// Use REACT_APP_BACKEND_URL if set, otherwise use relative URL (same domain)
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper function for API calls
async function apiCall(endpoint, options = {}) {
  const url = `${API_URL}${endpoint}`;
  
  const defaultOptions = {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  };
  
  // Add authorization header if token exists
  const token = localStorage.getItem('ohmnai_session_token');
  if (token) {
    defaultOptions.headers['Authorization'] = `Bearer ${token}`;
  }
  
  let response;
  try {
    response = await fetch(url, { ...defaultOptions, ...options });
  } catch (networkError) {
    throw new Error('Network error. Please check your connection and try again.');
  }
  
  if (!response.ok) {
    let errorDetail = 'An error occurred';
    try {
      const errorBody = await response.json();
      errorDetail = errorBody.detail || errorBody.message || `HTTP error! status: ${response.status}`;
    } catch (parseError) {
      // Response body wasn't JSON, use status code
      if (response.status === 520) {
        errorDetail = 'Server temporarily unavailable. Please try again.';
      } else if (response.status >= 500) {
        errorDetail = 'Server error. Please try again later.';
      } else {
        errorDetail = `Request failed with status ${response.status}`;
      }
    }
    throw new Error(errorDetail);
  }
  
  return response.json();
}

// ==================================================
// PROFILE API
// ==================================================

// Get or create a session ID for anonymous users
export function getSessionId() {
  let sessionId = localStorage.getItem('ohmnai_session_id');
  if (!sessionId) {
    sessionId = `anon_${Math.random().toString(36).substr(2, 12)}`;
    localStorage.setItem('ohmnai_session_id', sessionId);
  }
  return sessionId;
}

// Save profile draft (anonymous)
export async function saveProfileDraft(profileData) {
  const sessionId = getSessionId();
  return apiCall('/api/profile/draft', {
    method: 'POST',
    body: JSON.stringify({
      session_id: sessionId,
      values: profileData.selectedValues || [],
      resonance: profileData.personalValues || [],
      capacity: {
        time: profileData.capacity?.time || 'none',
        money: profileData.capacity?.money || 'none',
        attention: profileData.capacity?.attention || 'low'
      },
      goal_text: profileData.goalText || null,
      boundaries: {
        avoid_topics: profileData.boundaries?.avoidTopics || []
      }
    })
  });
}

// Get profile (for authenticated or anonymous users)
export async function getProfile() {
  const sessionId = getSessionId();
  const token = localStorage.getItem('ohmnai_session_token');
  
  const params = token ? '' : `?session_id=${sessionId}`;
  return apiCall(`/api/profile${params}`);
}

// Get profile completeness status
export async function getProfileStatus(sessionId = null) {
  const params = sessionId ? `?session_id=${sessionId}` : '';
  return apiCall(`/api/profile/status${params}`);
}

// Get comprehensive dashboard summary (profile, recent action, history)
export async function getDashboardSummary() {
  const sessionId = getSessionId();
  const token = localStorage.getItem('ohmnai_session_token');
  const params = token ? '' : `?session_id=${sessionId}`;
  return apiCall(`/api/dashboard/summary${params}`);
}

// ==================================================
// ACTION SUGGESTIONS API
// ==================================================

// Request an action suggestion
export async function requestActionSuggestion() {
  const sessionId = getSessionId();
  return apiCall('/api/actions/request', {
    method: 'POST',
    body: JSON.stringify({
      session_id: sessionId
    })
  });
}

// Get latest action suggestion
export async function getLatestAction() {
  const sessionId = getSessionId();
  const token = localStorage.getItem('ohmnai_session_token');
  
  const params = token ? '' : `?session_id=${sessionId}`;
  return apiCall(`/api/actions/latest${params}`);
}

// Record action response (accept/skip)
export async function recordActionResponse(suggestionId, response) {
  return apiCall(`/api/actions/${suggestionId}/response`, {
    method: 'POST',
    body: JSON.stringify({ response })
  });
}

// ==================================================
// AUTH API
// ==================================================

// Request magic link email
export async function requestMagicLink(email) {
  return apiCall('/api/auth/magic-link/request', {
    method: 'POST',
    body: JSON.stringify({ email })
  });
}

// Verify magic link token
export async function verifyMagicLink(token, anonymousSessionId = null) {
  const response = await apiCall('/api/auth/magic-link/verify', {
    method: 'POST',
    body: JSON.stringify({ 
      token,
      session_id: anonymousSessionId
    })
  });
  
  // Store session token
  if (response.session_token) {
    localStorage.setItem('ohmnai_session_token', response.session_token);
  }
  
  // Store user data
  if (response.user) {
    localStorage.setItem('ohmnai_user', JSON.stringify(response.user));
  }
  
  return response;
}

// Exchange Emergent session for user data
export async function exchangeSession(emergentSessionId, anonymousSessionId = null) {
  const params = anonymousSessionId ? `?session_id=${anonymousSessionId}` : '';
  const response = await fetch(`${API_URL}/api/auth/session${params}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-Session-ID': emergentSessionId
    }
  });
  
  if (!response.ok) {
    throw new Error('Failed to exchange session');
  }
  
  const data = await response.json();
  
  // Store session token
  if (data.session_token) {
    localStorage.setItem('ohmnai_session_token', data.session_token);
  }
  
  // Store user data
  if (data.user) {
    localStorage.setItem('ohmnai_user', JSON.stringify(data.user));
  }
  
  return data;
}

// Get current user
export async function getCurrentUser() {
  return apiCall('/api/auth/me');
}

// Logout
export async function logout() {
  try {
    await apiCall('/api/auth/logout', { method: 'POST' });
  } finally {
    localStorage.removeItem('ohmnai_session_token');
    localStorage.removeItem('ohmnai_user');
  }
}

// ==================================================
// SUBSCRIPTION API
// ==================================================

// Create checkout session
export async function createCheckoutSession() {
  const originUrl = window.location.origin;
  return apiCall('/api/subscriptions/checkout', {
    method: 'POST',
    body: JSON.stringify({ origin_url: originUrl })
  });
}

// Get checkout status
export async function getCheckoutStatus(sessionId) {
  return apiCall(`/api/subscriptions/checkout/status/${sessionId}`);
}

// Get subscription status
export async function getSubscriptionStatus() {
  return apiCall('/api/subscriptions/status');
}

// Cancel subscription
export async function cancelSubscription() {
  return apiCall('/api/subscriptions/cancel', { method: 'POST' });
}

// Reactivate subscription
export async function reactivateSubscription() {
  return apiCall('/api/subscriptions/reactivate', { method: 'POST' });
}

// Create Stripe Customer Portal session
export async function createSubscriptionPortal() {
  const originUrl = window.location.origin;
  return apiCall('/api/subscriptions/portal', {
    method: 'POST',
    body: JSON.stringify({ origin_url: originUrl })
  });
}

// ==================================================
// ACCOUNT API
// ==================================================

// Export user data
export async function exportAccountData() {
  const response = await fetch(`${API_URL}/api/account/export`, {
    credentials: 'include',
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('ohmnai_session_token')}`
    }
  });
  
  if (!response.ok) {
    throw new Error('Failed to export data');
  }
  
  const data = await response.json();
  
  // Create and download file
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `ohmnai-data-${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  
  return data;
}

// Delete account
export async function deleteAccount() {
  return apiCall('/api/account', { method: 'DELETE' });
}

// ==================================================
// ADMIN API
// ==================================================

// Get admin user info (returns 404 if not admin)
export async function getAdminMe() {
  return apiCall('/api/admin/me');
}

// List/search users
export async function adminListUsers(query = '', filter = '', page = 1, limit = 20) {
  const params = new URLSearchParams();
  if (query) params.append('query', query);
  if (filter) params.append('filter', filter);
  params.append('page', page.toString());
  params.append('limit', limit.toString());
  
  return apiCall(`/api/admin/users?${params.toString()}`);
}

// Get user detail
export async function adminGetUser(userId) {
  return apiCall(`/api/admin/users/${userId}`);
}

// Toggle user disabled status
export async function adminToggleDisabled(userId, disabled, reason) {
  return apiCall(`/api/admin/users/${userId}/disable`, {
    method: 'POST',
    body: JSON.stringify({ disabled, reason })
  });
}

// Toggle circle override
export async function adminToggleCircleOverride(userId, enabled, reason) {
  return apiCall(`/api/admin/users/${userId}/circle-override`, {
    method: 'POST',
    body: JSON.stringify({ enabled, reason })
  });
}

// Update user roles
export async function adminUpdateRoles(userId, add, remove, reason) {
  return apiCall(`/api/admin/users/${userId}/roles`, {
    method: 'POST',
    body: JSON.stringify({ add, remove, reason })
  });
}

// Get user audit log
export async function adminGetUserAudit(userId, limit = 20, skip = 0) {
  return apiCall(`/api/admin/users/${userId}/audit?limit=${limit}&skip=${skip}`);
}

// Resync subscription from Stripe
export async function adminResyncStripe(userId, email = null, stripeCustomerId = null) {
  return apiCall(`/api/admin/users/${userId}/resync-stripe`, {
    method: 'POST',
    body: JSON.stringify({ 
      email: email || undefined, 
      stripe_customer_id: stripeCustomerId || undefined 
    })
  });
}
